<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'ChatDeskDesk' }" class="btn btn-secondary">返回工作台</router-link>
        <button v-if="showCannedReplies" class="btn btn-primary" type="button" @click="startCreate">新增罐頭回覆</button>
      </div>
    </div>

    <div class="page-container settings-sections">
      <div v-if="!isCannedRepliesPage" class="page-tabs" role="tablist" aria-label="ChatDesk 設定">
        <button
          type="button"
          class="page-tab"
          :class="{ active: settingsTab === 'seats' }"
          role="tab"
          :aria-selected="settingsTab === 'seats'"
          @click="settingsTab = 'seats'"
        >座席設定</button>
        <button
          type="button"
          class="page-tab"
          :class="{ active: settingsTab === 'notify' }"
          role="tab"
          :aria-selected="settingsTab === 'notify'"
          @click="settingsTab = 'notify'"
        >轉派離線通知</button>
        <button
          type="button"
          class="page-tab"
          :class="{ active: settingsTab === 'replies' }"
          role="tab"
          :aria-selected="settingsTab === 'replies'"
          @click="settingsTab = 'replies'"
        >罐頭回覆</button>
      </div>

      <section v-if="!isCannedRepliesPage && settingsTab === 'seats'" class="info-section">
        <h2>座席設定</h2>
        <div v-if="subscriptionLoading" class="loading-inline">載入中...</div>
        <template v-else>
          <div v-if="seats" class="seats-summary">
            <div class="seat-row"><span class="label">授權座席</span><span class="value">{{ seats.totalSeats }} 席</span></div>
            <div class="seat-row"><span class="label">內含</span><span class="value">{{ seats.includedSeats }} 席</span></div>
            <div class="seat-row"><span class="label">加購</span><span class="value">{{ seats.extraSeats }} 席</span></div>
            <div class="seat-row"><span class="label">已使用</span><span class="value">{{ seats.usedSeats }} 席</span></div>
          </div>
          <p>ChatDesk 含 3 席客服。加購座席（NT$250／席／月）請聯繫平台管理員。</p>
          <p class="hint">具 <code>chatdesk:reply</code> 權限且可進入工作台的帳號數不可超過授權座席數。</p>
        </template>
      </section>

      <section v-if="!isCannedRepliesPage && settingsTab === 'notify'" class="info-section">
        <h2>轉派離線通知</h2>
        <p class="hint">被轉派的客服若不在線上，依這裡的模式通知。Email 使用帳號 Email。LINE 與簡訊請到員工帳號編輯頁填寫 LINE 好友與手機。</p>
        <div v-if="notifyLoading" class="loading-inline">載入通知設定...</div>
        <template v-else>
          <div class="notify-mode" role="radiogroup" aria-label="通知模式">
            <label>
              <input v-model="notifyMode" type="radio" value="sequential" :disabled="!canEditNotify" />
              依序通知
            </label>
            <label>
              <input v-model="notifyMode" type="radio" value="all" :disabled="!canEditNotify" />
              全部通知
            </label>
          </div>
          <p class="hint">{{ notifyModeHint }}</p>
          <ol class="notify-order">
            <li v-for="(channel, index) in notifyOrder" :key="channel" class="notify-row">
              <label>
                <input v-model="notifyEnabled[channel]" type="checkbox" :disabled="!canEditNotify" />
                {{ notifyLabel(channel) }}
              </label>
              <span v-if="notifyMode === 'sequential'" class="notify-move">
                <button type="button" class="btn btn-secondary btn-sm" :disabled="!canEditNotify || index === 0" @click="moveNotify(index, -1)">上移</button>
                <button type="button" class="btn btn-secondary btn-sm" :disabled="!canEditNotify || index === notifyOrder.length - 1" @click="moveNotify(index, 1)">下移</button>
              </span>
            </li>
          </ol>
          <button
            v-if="canEditNotify"
            class="btn btn-primary btn-sm"
            type="button"
            :disabled="notifySaving"
            @click="saveNotifySettings"
          >{{ notifySaving ? '儲存中...' : '儲存通知設定' }}</button>
          <p v-else class="hint">僅客戶管理員可修改通知設定。</p>
        </template>
      </section>

      <section v-if="showCannedReplies" class="info-section">
        <div class="scope-notice">
          <i class="ri-information-line" aria-hidden="true"></i>
          <span>團隊罐頭提供所有客服使用；個人罐頭只會顯示給自己。</span>
        </div>

        <div v-if="repliesLoading" class="loading-inline">載入罐頭回覆...</div>
        <template v-else>
          <div class="reply-tabs" role="tablist" aria-label="罐頭回覆範圍">
            <button
              type="button"
              class="reply-tab"
              :class="{ active: activeReplyTab === 'personal' }"
              role="tab"
              :aria-selected="activeReplyTab === 'personal'"
              @click="activeReplyTab = 'personal'"
            >
              我的個人罐頭
              <span class="tab-count">{{ personalReplies.length }}</span>
            </button>
            <button
              type="button"
              class="reply-tab"
              :class="{ active: activeReplyTab === 'team' }"
              role="tab"
              :aria-selected="activeReplyTab === 'team'"
              @click="activeReplyTab = 'team'"
            >
              團隊共用
              <span class="tab-count">{{ teamReplies.length }}</span>
            </button>
          </div>

          <div class="reply-group" role="tabpanel">
            <ReplyEmpty v-if="activeReplies.length === 0" :text="activeReplyEmptyText" />
            <div v-else class="reply-list">
              <article v-for="reply in activeReplies" :key="reply.id" class="reply-card">
                <div class="reply-main">
                  <div class="reply-title">
                    {{ reply.title }}
                    <span v-if="!reply.isActive" class="badge-inactive">已停用</span>
                    <span v-if="reply.sourceTemplateDeleted" class="badge-source-deleted">來源已刪除</span>
                  </div>
                  <div class="reply-preview">{{ previewText(reply) }}</div>
                </div>
                <div v-if="reply.scope === 'personal' || canManageTeam" class="reply-actions">
                  <button type="button" class="btn-link" @click="startEdit(reply)">編輯</button>
                  <button type="button" class="btn-link danger" @click="removeReply(reply)">刪除</button>
                </div>
              </article>
            </div>
          </div>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { chatdeskService } from '@/api/modules/chatdesk.js';
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings.js';
import authService from '@/helpers/authservice/authService.js';

const NOTIFY_CHANNELS = ['email', 'line', 'sms'];
const NOTIFY_LABELS = { email: 'Email', line: 'LINE', sms: '簡訊' };

const ReplyEmpty = {
  props: { text: { type: String, required: true } },
  template: '<div class="empty-replies">{{ text }}</div>'
};

export default {
  name: 'ChatDeskSettings',
  components: { ReplyEmpty },
  setup() {
    const route = useRoute();
    const { pageTitle } = usePageTitle(
      route.name === 'ChatDeskCannedReplies' ? 'ChatDesk 罐頭回覆' : 'ChatDesk 設定'
    );
    return { pageTitle };
  },
  data() {
    return {
      subscriptionLoading: true,
      repliesLoading: true,
      seats: null,
      replies: [],
      activeReplyTab: 'personal',
      settingsTab: 'seats',
      notifyLoading: true,
      notifySaving: false,
      notifyOrder: [...NOTIFY_CHANNELS],
      notifyEnabled: { email: true, line: false, sms: false },
      notifyMode: 'sequential'
    };
  },
  computed: {
    isCannedRepliesPage() {
      return this.$route.name === 'ChatDeskCannedReplies';
    },
    showCannedReplies() {
      return this.isCannedRepliesPage || this.settingsTab === 'replies';
    },
    canManageTeam() {
      return authService.hasPermission('chatdesk:manage');
    },
    canEditNotify() {
      return authService.getRole() === 'client_owner';
    },
    notifyModeHint() {
      return this.notifyMode === 'all'
        ? '勾選的管道都會送出通知。'
        : '依下面順序嘗試。某一管道送出成功後，就不再使用後面的管道。';
    },
    teamReplies() {
      return this.replies.filter(item => item.scope === 'team');
    },
    personalReplies() {
      return this.replies.filter(item => item.scope === 'personal');
    },
    activeReplies() {
      return this.activeReplyTab === 'team' ? this.teamReplies : this.personalReplies;
    },
    activeReplyEmptyText() {
      return this.activeReplyTab === 'team'
        ? '尚未建立團隊罐頭回覆'
        : '尚未建立個人罐頭回覆';
    }
  },
  async mounted() {
    const tasks = [this.loadReplies()];
    if (!this.isCannedRepliesPage) {
      tasks.push(this.loadSubscription(), this.loadNotifySettings());
    }
    await Promise.all(tasks);
  },
  methods: {
    async loadSubscription() {
      this.subscriptionLoading = true;
      try {
        const res = await chatdeskService.getSubscription();
        this.seats = res?.data?.seats || null;
      } catch (_) {
        this.seats = null;
      } finally {
        this.subscriptionLoading = false;
      }
    },
    notifyLabel(channel) {
      return NOTIFY_LABELS[channel] || channel;
    },
    moveNotify(index, delta) {
      const next = index + delta;
      if (next < 0 || next >= this.notifyOrder.length) return;
      const order = [...this.notifyOrder];
      const [item] = order.splice(index, 1);
      order.splice(next, 0, item);
      this.notifyOrder = order;
    },
    async loadNotifySettings() {
      this.notifyLoading = true;
      try {
        const res = await clientSettingsService.get(NAMESPACES.CHATDESK);
        const saved = Array.isArray(res?.data?.settings?.transferNotifyChannels)
          ? res.data.settings.transferNotifyChannels
          : ['email'];
        const enabled = saved.filter(channel => NOTIFY_CHANNELS.includes(channel));
        const rest = NOTIFY_CHANNELS.filter(channel => !enabled.includes(channel));
        this.notifyOrder = [...enabled, ...rest];
        this.notifyEnabled = NOTIFY_CHANNELS.reduce((acc, channel) => {
          acc[channel] = enabled.includes(channel);
          return acc;
        }, {});
        this.notifyMode = res?.data?.settings?.transferNotifyMode === 'all' ? 'all' : 'sequential';
      } catch (error) {
        await Swal.fire('載入失敗', error.response?.data?.message || error.message, 'error');
      } finally {
        this.notifyLoading = false;
      }
    },
    async saveNotifySettings() {
      const transferNotifyChannels = this.notifyOrder.filter(channel => this.notifyEnabled[channel]);
      const transferNotifyMode = this.notifyMode === 'all' ? 'all' : 'sequential';
      this.notifySaving = true;
      try {
        await clientSettingsService.merge(NAMESPACES.CHATDESK, { transferNotifyChannels, transferNotifyMode });
        await Swal.fire('已儲存', '轉派離線通知設定已更新', 'success');
      } catch (error) {
        await Swal.fire('儲存失敗', error.response?.data?.message || error.message, 'error');
      } finally {
        this.notifySaving = false;
      }
    },
    async loadReplies() {
      this.repliesLoading = true;
      try {
        const res = await chatdeskService.listCannedReplies({ activeOnly: false });
        this.replies = res?.data?.items || [];
      } catch (error) {
        await Swal.fire('載入失敗', error.response?.data?.message || error.message, 'error');
      } finally {
        this.repliesLoading = false;
      }
    },
    startCreate() {
      this.$router.push({ name: 'ChatDeskCannedReplyCreate' });
    },
    startEdit(reply) {
      this.$router.push({
        name: 'ChatDeskCannedReplyEdit',
        params: { id: reply.id }
      });
    },
    async removeReply(reply) {
      const result = await Swal.fire({
        title: `刪除「${reply.title}」？`,
        text: '刪除後無法復原',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '刪除',
        cancelButtonText: '取消'
      });
      if (!result.isConfirmed) return;
      try {
        await chatdeskService.deleteCannedReply(reply.id);
        await this.loadReplies();
      } catch (error) {
        await Swal.fire('刪除失敗', error.response?.data?.message || error.message, 'error');
      }
    },
    previewText(reply) {
      if (reply.messageType === 'line_template') {
        return `LINE 範本 · ${reply.lineMsgTemplateSnapshot?.name || '已保存範本'}`;
      }
      const typeLabel = reply.messageType === 'image' ? '圖片' : (reply.messageType === 'file' ? '檔案' : '文字');
      return `${typeLabel} · ${reply.content || reply.fileName || '無文字說明'}`;
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';
.settings-sections { display: grid; gap: 20px; }
.page-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e7eb;
}
.page-tab {
  padding: 10px 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
}
.page-tab:hover { color: #1976d2; }
.page-tab.active { color: #1976d2; border-bottom-color: #1976d2; font-weight: 600; }
.info-section {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
.info-section h2 { font-size: 20px; font-weight: 600; margin: 0 0 12px; }
.header-actions { display: flex; align-items: center; gap: 10px; }
.hint { color: #666; font-size: 14px; margin: 8px 0 16px; }
.notify-mode { display: flex; gap: 16px; margin: 8px 0 4px; }
.notify-mode label { display: flex; align-items: center; gap: 8px; margin: 0; font-weight: 600; }
.notify-order { list-style: none; margin: 0 0 16px; padding: 0; display: grid; gap: 8px; }
.notify-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.notify-row label { display: flex; align-items: center; gap: 8px; margin: 0; }
.notify-move { display: flex; gap: 8px; }
.scope-notice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid #b9d8f4;
  border-left: 4px solid #1976d2;
  border-radius: 6px;
  background: #eef7ff;
  color: #24577f;
  font-size: 14px;
  font-weight: 500;
}
.scope-notice i { flex: 0 0 auto; color: #1976d2; font-size: 20px; }
.seats-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 6px;
}
.seat-row { display: flex; justify-content: space-between; font-size: 14px; }
.seat-row .label { color: #666; }
.seat-row .value { font-weight: 600; }
.loading-inline { color: #666; padding: 16px 0; }
.btn-link { border: 0; background: none; color: #1976d2; cursor: pointer; }
.reply-actions { display: flex; gap: 8px; }
.btn { display: inline-flex; align-items: center; justify-content: center; padding: 9px 16px; border-radius: 4px; text-decoration: none; border: 0; cursor: pointer; }
.btn-primary { background: #1976d2; color: white; }
.btn-secondary { background: #edf1f5; color: #34495e; }
.reply-tabs {
  display: flex;
  gap: 4px;
  margin-top: 20px;
  border-bottom: 1px solid #dce2e8;
}
.reply-tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 14px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #687481;
  font-weight: 600;
  cursor: pointer;
}
.reply-tab.active {
  border-bottom-color: #1976d2;
  color: #1976d2;
}
.tab-count {
  min-width: 20px;
  padding: 1px 6px;
  border-radius: 10px;
  background: #edf1f5;
  color: inherit;
  font-size: 12px;
  text-align: center;
}
.reply-group { margin-top: 16px; }
.reply-list { display: grid; gap: 8px; }
.reply-card { display: flex; justify-content: space-between; gap: 16px; padding: 13px 14px; border: 1px solid #e1e6eb; border-radius: 7px; }
.reply-main { min-width: 0; }
.reply-title { font-weight: 600; }
.reply-preview { color: #6b7280; font-size: 13px; margin-top: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.badge-inactive { display: inline-block; margin-left: 5px; padding: 2px 6px; border-radius: 10px; background: #eee; color: #666; font-size: 11px; }
.badge-source-deleted {
  display: inline-block; margin-left: 5px; padding: 2px 6px; border-radius: 10px;
  background: #fff1f0; color: #b42318; font-size: 11px;
}
.danger { color: #c62828; }
.empty-replies { padding: 14px; color: #7a8490; background: #f8f9fa; border-radius: 6px; font-size: 14px; }
@media (max-width: 576px) {
  .seats-summary { grid-template-columns: 1fr; }
  .header-actions { flex-wrap: wrap; justify-content: flex-end; }
  .reply-card { align-items: stretch; flex-direction: column; }
}
</style>
